import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import AddTaskModal from './components/AddTaskModal';
import TaskList from './components/TaskList';

interface Task {
  id: number;
  name: string;
  title: string;
  description: string;
  owner: string;
  assignedTo: string;
  deadline: string;
  dueDate: string;
  status: string;
  priority: string;
  createdDate: string;
}

export default function Tasks() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    if (!isLoggedIn) {
      navigate('/login');
    }

    // Load tasks from localStorage
    const storedTasks = localStorage.getItem('tasks');
    if (storedTasks) {
      setTasks(JSON.parse(storedTasks));
    } else {
      // Initialize with sample data
      const initialTasks = [
        {
          id: 1,
          name: 'Review Q4 Financial Reports',
          title: 'Review Q4 Financial Reports',
          description: 'Analyze and review the quarterly financial reports for Q4 2023. Prepare summary for board meeting and identify key trends and areas of concern.',
          owner: 'Kalyan',
          assignedTo: 'Kalyan',
          deadline: '2024-01-20',
          dueDate: '2024-01-20',
          status: 'in-progress',
          priority: 'high',
          createdDate: '2024-01-10',
        },
        {
          id: 2,
          name: 'Update Product Inventory',
          title: 'Update Product Inventory',
          description: 'Complete inventory check for all Moringa products. Update stock levels in the system and identify items that need reordering.',
          owner: 'Shirish',
          assignedTo: 'Shirish',
          deadline: '2024-01-22',
          dueDate: '2024-01-22',
          status: 'pending',
          priority: 'medium',
          createdDate: '2024-01-12',
        },
        {
          id: 3,
          name: 'Prepare Marketing Materials',
          title: 'Prepare Marketing Materials',
          description: 'Design and prepare marketing materials for the upcoming wellness campaign. Include brochures, social media content, and email templates.',
          owner: 'Amol',
          assignedTo: 'Amol',
          deadline: '2024-01-25',
          dueDate: '2024-01-25',
          status: 'completed',
          priority: 'low',
          createdDate: '2024-01-08',
        },
        {
          id: 4,
          name: 'Conduct Safety Training',
          title: 'Conduct Safety Training',
          description: 'Organize and conduct safety training session for all farming staff. Cover equipment handling, emergency procedures, and safety protocols.',
          owner: 'John Doe',
          assignedTo: 'John Doe',
          deadline: '2024-01-18',
          dueDate: '2024-01-18',
          status: 'in-progress',
          priority: 'high',
          createdDate: '2024-01-11',
        },
        {
          id: 5,
          name: 'Update Company Policies',
          title: 'Update Company Policies',
          description: 'Review and update company policies document to reflect new regulations and best practices. Get approval from management team.',
          owner: 'Sarah Smith',
          assignedTo: 'Sarah Smith',
          deadline: '2024-01-30',
          dueDate: '2024-01-30',
          status: 'pending',
          priority: 'medium',
          createdDate: '2024-01-13',
        },
        {
          id: 6,
          name: 'Website Content Update',
          title: 'Website Content Update',
          description: 'Update website content with new product information, testimonials, and blog posts about Moringa health benefits.',
          owner: 'Mike Johnson',
          assignedTo: 'Mike Johnson',
          deadline: '2024-01-28',
          dueDate: '2024-01-28',
          status: 'pending',
          priority: 'low',
          createdDate: '2024-01-14',
        },
      ];
      setTasks(initialTasks);
      localStorage.setItem('tasks', JSON.stringify(initialTasks));
      window.dispatchEvent(new Event('tasksUpdated'));
    }
  }, [navigate]);

  const handleAddTask = (task: Omit<Task, 'id'>) => {
    const newTask = {
      ...task,
      id: Date.now(),
      title: task.name,
      assignedTo: task.owner,
      dueDate: task.deadline,
      status: task.status.toLowerCase().replace(' ', '-'),
      priority: task.priority.toLowerCase(),
    };
    const updatedTasks = [...tasks, newTask];
    setTasks(updatedTasks);
    localStorage.setItem('tasks', JSON.stringify(updatedTasks));
    setIsModalOpen(false);
    window.dispatchEvent(new Event('tasksUpdated'));
  };

  const handleDeleteTask = (id: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      const updatedTasks = tasks.filter(t => t.id !== id);
      setTasks(updatedTasks);
      localStorage.setItem('tasks', JSON.stringify(updatedTasks));
      window.dispatchEvent(new Event('tasksUpdated'));
    }
  };

  const handleUpdateTask = (updatedTask: Task) => {
    const taskWithSync = {
      ...updatedTask,
      title: updatedTask.name,
      assignedTo: updatedTask.owner,
      dueDate: updatedTask.deadline,
      status: updatedTask.status.toLowerCase().replace(' ', '-'),
      priority: updatedTask.priority.toLowerCase(),
    };
    const updatedTasks = tasks.map(t =>
      t.id === taskWithSync.id ? taskWithSync : t
    );
    setTasks(updatedTasks);
    localStorage.setItem('tasks', JSON.stringify(updatedTasks));
    window.dispatchEvent(new Event('tasksUpdated'));
  };

  const getStatusCount = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'In Progress': 'in-progress',
      'Pending': 'pending',
      'Completed': 'completed'
    };
    return tasks.filter((task) => task.status === statusMap[status] || task.status === status).length;
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <DashboardHeader />
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Task Management</h1>
                <p className="text-sm text-gray-600">Manage and track all your tasks</p>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Add New Task
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1">TOTAL TASKS</p>
                    <p className="text-2xl font-bold text-gray-900">{tasks.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <i className="ri-task-line text-2xl text-teal-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1">IN PROGRESS</p>
                    <p className="text-2xl font-bold text-blue-600">{getStatusCount('In Progress')}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-blue-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1">COMPLETED</p>
                    <p className="text-2xl font-bold text-green-600">{getStatusCount('Completed')}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-2xl text-green-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1">PENDING</p>
                    <p className="text-2xl font-bold text-gray-600">{getStatusCount('Pending')}</p>
                  </div>
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <i className="ri-pause-circle-line text-2xl text-gray-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              </div>
            </div>

            <TaskList tasks={tasks} onDelete={handleDeleteTask} onUpdate={handleUpdateTask} />
          </div>
        </main>
      </div>

      <AddTaskModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdd={handleAddTask} />
    </div>
  );
}
